import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode.react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PAIRABLE_CHANNELS = ["whatsapp", "whatsmeow", "wuzapi"];

const WaCallsPairModal = ({ open, onClose, whatsApps = [], onChanged }) => {
  const [whatsappId, setWhatsappId] = useState("");
  const [state, setState] = useState("idle"); // idle | starting | qr | paired | expired
  const [qr, setQr] = useState(null);
  const timer = useRef(null);

  const options = whatsApps.filter((w) => PAIRABLE_CHANNELS.includes(w.channel));
  const selected = options.find((w) => w.id === whatsappId);
  const hasCalls = !!selected?.wavoip;

  const stopPolling = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };

  useEffect(() => {
    if (!open) {
      stopPolling();
      setWhatsappId("");
      setState("idle");
      setQr(null);
    }
    return stopPolling;
  }, [open]);

  const poll = (id) => {
    stopPolling();
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/call/wacalls/${id}/pair`);
        setState(data.state);
        setQr(data.qr || null);
        if (data.state === "paired") {
          stopPolling();
          toast.success("Ligações ativadas para esta conexão.");
          if (onChanged) onChanged();
        } else if (data.state === "idle" || data.state === "expired" || data.state === "error") {
          stopPolling();
        }
      } catch (err) {
        stopPolling();
        toastError(err);
      }
    }, 2000);
  };

  const handleStart = async () => {
    setState("starting");
    setQr(null);
    try {
      await api.post(`/call/wacalls/${whatsappId}/pair`);
      poll(whatsappId);
    } catch (err) {
      setState("idle");
      toastError(err);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete(`/call/wacalls/${whatsappId}`);
      toast.success("Ligações removidas desta conexão.");
      setState("idle");
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ativar ligações (WhatsApp)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Escolha a conexão e leia o QR Code com o celular desse número, em
          Aparelhos conectados. Isso adiciona um aparelho dedicado às ligações;
          as mensagens continuam pela conexão normal.
        </Typography>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          label="Conexão"
          value={whatsappId}
          disabled={state === "starting" || state === "qr"}
          onChange={(e) => {
            setWhatsappId(e.target.value);
            setState("idle");
            setQr(null);
          }}
        >
          {options.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
              {w.wavoip ? " (ligações ativas)" : ""}
            </MenuItem>
          ))}
        </TextField>

        <Box mt={2} display="flex" justifyContent="center" alignItems="center" minHeight={220}>
          {state === "starting" && <CircularProgress />}
          {state === "qr" && qr && <QRCode value={qr} size={220} />}
          {state === "qr" && !qr && <CircularProgress />}
          {state === "paired" && (
            <Typography style={{ color: "#2e7d32", fontWeight: 600 }}>
              Pareado! Já é possível ligar por esta conexão.
            </Typography>
          )}
          {state === "expired" && (
            <Typography color="error">O QR Code expirou. Gere outro.</Typography>
          )}
          {state === "idle" && hasCalls && (
            <Typography style={{ color: "#2e7d32" }}>Ligações já ativas nesta conexão.</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {hasCalls && state !== "starting" && state !== "qr" && (
          <Button color="secondary" onClick={handleRemove}>
            Remover ligações
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!whatsappId || hasCalls || state === "starting" || state === "qr" || state === "paired"}
          onClick={handleStart}
        >
          Gerar QR Code
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaCallsPairModal;
